import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import { Link } from "react-router-dom";
import { useHolidayMaster } from "../../contexts/HolidayContext";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isToday, isSameYear } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PublicHolidayCalendar = () => {
  const { holidays, fetchHolidays, pagination } = useHolidayMaster();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDateDetails, setSelectedDateDetails] = useState(null);
  const token = getToken();
  const { roles } = useContext(AuthContext);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  // Fetch holidays for the current month
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    fetchHolidays(1, 1000); // Fetch all holidays for the year
  }, [currentDate]);

  const fetchAttendance = async () => {
    try {
      const response = await axios.get('https://hrmspayroll.prepstripe.com/api/my-attendance', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = response.data;
      setAttendanceData(data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [token]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["Day", "Date", "Occasion"];
    const tableRows = holidaysForCurrentYear.map((holiday) => [
      holiday.day,
      holiday.date ? format(new Date(holiday.date), "dd-MMM-yyyy") : "N/A",
      holiday.occasion,
    ]);
  
    doc.text("Public Holiday Calendar", 14, 15);
    autoTable(doc, {
      startY: 20,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });
    doc.save("Public_Holiday_Calendar.pdf");
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    if (currentDate.getMonth() === 0) return; // Clamp at January of current year
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    if (currentDate.getMonth() === 11) return; // Clamp at December of current year
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  // Check if a date is a holiday
  const getHolidayForDate = (date) => {
    return holidaysForCurrentYear.find(holiday => 
      holiday.date && isSameDay(new Date(holiday.date), date)
    );
  };

  // Filter holidays for the current year only
  const holidaysForCurrentYear = useMemo(() => {
    return holidays.filter(holiday => {
      if (!holiday.date) return false;
      return isSameYear(new Date(holiday.date), currentDate);
    });
  }, [holidays, currentDate]);

  const getAttendanceForDate = (date) => {
    const monthStr = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    const selectedMonthData = attendanceData?.monthly_attendance?.find(
      (entry) => entry.month === monthStr
    );

    return selectedMonthData?.attendance?.find((record) => {
      const recordDate = new Date(record.attendance_date);
      return isSameDay(recordDate, date);
    });
  };

  const handleDayClick = (date) => {
    const holiday = getHolidayForDate(date);
    const attendance = getAttendanceForDate(date);
    setSelectedDateDetails({ date, holiday, attendance });
  };

  const closePopup = () => {
    setSelectedDateDetails(null);
  };

  // Calendar header
  const CalendarHeader = () => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const handleMonthSelect = (e) => {
      const nextMonthIndex = parseInt(e.target.value, 10);
      const year = currentDate.getFullYear();
      setCurrentDate(new Date(year, nextMonthIndex, 1));
    };
    const isPrevDisabled = currentDate.getMonth() === 0;
    const isNextDisabled = currentDate.getMonth() === 11;
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {format(currentDate, 'MMMM yyyy')}
          </h1>
          <select
            value={currentDate.getMonth()}
            onChange={handleMonthSelect}
            className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-200"
          >
            {monthNames.map((name, idx) => (
              <option key={name} value={idx}>{name}</option>
            ))}
          </select>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Today
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPreviousMonth}
            disabled={isPrevDisabled}
            className={`p-2 rounded-md transition-colors ${isPrevDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={goToNextMonth}
            disabled={isNextDisabled}
            className={`p-2 rounded-md transition-colors ${isNextDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
    );
  };

  // Day names header
  const DayNamesHeader = () => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div
            key={day}
            className="p-3 text-center text-sm font-semibold text-gray-600 dark:text-gray-400"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };

  // Calendar day component
  const CalendarDay = ({ date }) => {
    const isCurrentMonth = isSameMonth(date, currentDate);
    const isCurrentDay = isToday(date);
    const holiday = getHolidayForDate(date);
    const attendance = getAttendanceForDate(date);

    return (
      <div
        onClick={() => handleDayClick(date)}
        className={`
          min-h-[100px] p-2 border border-gray-200 dark:border-gray-700 relative cursor-pointer
          ${isCurrentMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
          ${isCurrentDay ? 'ring-2 ring-blue-500' : ''}
          hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
        `}
      >
        <div className="flex justify-between items-start">
          <span
            className={`
              text-sm font-medium
              ${isCurrentMonth ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}
              ${isCurrentDay ? 'text-blue-600 dark:text-blue-400' : ''}
            `}
          >
            {format(date, 'd')}
          </span>
          {holiday && (
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          )}
        </div>

        {holiday && (
          <div className="mt-1">
            <div className="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded truncate">
              {holiday.occasion}
            </div>
          </div>
        )}

        {attendance && (
          <div className="mt-1">
            <div
              className={`text-xs px-2 py-1 rounded truncate
                ${attendance.da_type === 'P' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}
                ${attendance.da_type === 'A' ? 'bg-red-700 text-red-100 dark:bg-red-900 dark:text-red-200' : ''}
                ${attendance.da_type === 'WO' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : ''}
                ${attendance.da_type === 'LWP' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : ''}
                ${attendance.da_type === 'HO' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : ''}
              `}
            >
              {attendance.da_type}
            </div>
          </div>
        )}
      </div>
    );
  };

  const attendanceCounts = useMemo(() => {
    const counts = { P: 0, A: 0, WO: 0, LWP: 0, HO: 0, PublicHoliday: 0 };
    const monthStr = `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`;
    const selectedMonthData = attendanceData?.monthly_attendance?.find(
      (entry) => entry.month === monthStr
    );

    selectedMonthData?.attendance?.forEach((record) => {
      if (counts[record.da_type] !== undefined) {
        counts[record.da_type] += 1;
      }
    });

    holidaysForCurrentYear.forEach((holiday) => {
      if (isSameMonth(new Date(holiday.date), currentDate)) {
        counts.PublicHoliday += 1;
      }
    });

    return counts;
  }, [attendanceData, holidaysForCurrentYear, currentDate]);

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 shadow-lg rounded-lg">

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">My Calendar</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Calendar Header */}
      <CalendarHeader />

      {/* Day Names */}
      <DayNamesHeader />

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, index) => (
          <CalendarDay key={index} date={date} />
        ))}
      </div>

      {/* Legend */}
      {/* <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Public Holiday</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full ring-2 ring-blue-500"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Today</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Present (P)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Week Off (WO)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Leave Without Pay (LWP)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-700 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Absent (A)</span>
          </div>
        </div>

        <button
          onClick={exportToPDF}
          disabled={holidaysForCurrentYear.length === 0}
          className={`${
            holidaysForCurrentYear.length === 0 
              ? "bg-gray-300 cursor-not-allowed" 
              : "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
          } text-white px-4 py-2 rounded-md transition-colors`}
        >
          Export as PDF
        </button>
      </div> */}

      <div className="mt-6 flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        {/* Legend Items */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-x-6">
          {/* <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full ring-2 ring-blue-500"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Today</span>
          </div> */}
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">P ({attendanceCounts.P})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">WO ({attendanceCounts.WO})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">LWP ({attendanceCounts.LWP})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-700 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">A ({attendanceCounts.A})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">HO ({attendanceCounts.HO})</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Public Holiday ({attendanceCounts.PublicHoliday})</span>
          </div>
        </div>
        
        {/* Export Button */}
        {/* <button
          onClick={exportToPDF}
          disabled={holidaysForCurrentYear.length === 0}
          className={`${
            holidaysForCurrentYear.length === 0
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
          } text-white px-4 py-2 rounded-md transition-colors w-full sm:w-auto text-sm sm:text-base`}
        >
          Export as PDF
        </button> */}
      </div>

      {/* Holiday List for Mobile */}
      <div className="mt-6 md:hidden">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Holidays in {format(currentDate, 'MMMM yyyy')}
        </h3>
        <div className="space-y-2">
          {holidaysForCurrentYear
            .filter(holiday => {
              if (!holiday.date) return false;
              const holidayDate = new Date(holiday.date);
              return isSameMonth(holidayDate, currentDate);
            })
            .map((holiday, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg"
              >
                <div>
                  <div className="font-medium text-red-800 dark:text-red-200">
                    {holiday.occasion}
                  </div>
                  <div className="text-sm text-red-600 dark:text-red-300">
                    {format(new Date(holiday.date), 'EEEE, MMMM d, yyyy')}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Popup for Date Details */}
      {selectedDateDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]">
          <div className="flex flex-col gap-6 bg-white dark:bg-gray-800 p-2.5 rounded-lg shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Details for {format(selectedDateDetails.date, 'EEEE, MMMM d, yyyy')}
            </h2>
            {selectedDateDetails.holiday && (
              <p className="text-md text-red-600 dark:text-red-300 mb-2">
                Holiday: {selectedDateDetails.holiday.occasion}
              </p>
            )}
            {selectedDateDetails.attendance && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Attendance: {selectedDateDetails.attendance.da_type}
              </p>
            )}
            <div className="flex justify-end items-center">
              <button
                onClick={closePopup}
                className="px-10 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PublicHolidayCalendar;
